import "firebase/auth"
import { navigate } from "gatsby"
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"
import { toast } from "react-toastify"
import client from "../apollo/client"
import {
  DELETE_ENTRY,
  DELETE_USER,
  UPDATE_USER_SETTINGS,
} from "../apollo/mutations"
import {
  EXPORT_DATA,
  GET_ENTRIES,
  SELECTED_UPGRADE,
  USER_PLAN,
} from "../apollo/queries"
import { getCurrentUser, signOut } from "./auth"
import log from "./log"
import { trackEvent } from "./track"

import VERSION from "../version"
import cache from "../apollo/cache"
import { useEffect, useRef } from "react"

export const safeReadQuery = (query, defaultData, variables) => {
  let value
  // Attempt to retrieve item from client state
  try {
    // If present, set to variable.
    if (variables) value = cache.readQuery({ query, variables })
    else value = cache.readQuery({ query })
    // log("log", [
    //   "Client data: safeReadQuery Exists",
    //   {
    //     query,
    //     key,
    //     value,
    //     defaultData,
    //   },
    // ])
  } catch (e) {
    debugger //_
    // If item is not found in cache data, write it with default value
    client.writeData({ data: defaultData })
    // log("log", [
    //   "Client data: safeReadQuery notExists",
    //   {
    //     query,
    //     key,
    //     defaultData,
    //   },
    // ])
  }
  return value ? value : defaultData
}

export const extractNameFromProvider = provider => {
  let first = "",
    last = ""

  if (!provider || !provider.displayName) {
    log("warn", [
      "extractNameFromProvider failed because `provider` parameter is undefined or not formed as expected",
      { provider },
    ])
    return {
      first: "",
      last: "",
    }
  }

  let displayName = provider.displayName.split(" ")
  if (displayName.length > 2) {
    first = displayName[0]
    displayName
      .filter(x => x !== first)
      .forEach((y, i) => (last += `${displayName[i]} `))
  } else {
    first = displayName[0] ? displayName[0].trim() : ""
    last = displayName[1] ? displayName[1].trim() : ""
  }
  return { first, last }
}

export const setMonthlyReviewAvailable = (month, year) => {
  console.log("TCL: setMonthlyReviewAvailable -> month, year", month, year)
  // Send klaviyo event one time
  trackEvent(
    "Guided Reflection Available",
    {
      guide: "Monthly Review",
      month: month.charAt(0).toUpperCase() + month.slice(1),
    },
    { month, year }
  )
}

export const setMonthlyReviewStarted = (month, year) => {
  console.log("TCL: setMonthlyReviewStarted -> month year", month, year)
  // Send klaviyo event one time
  trackEvent(
    "Guided Reflection Started",
    {
      guide: "Monthly Review",
      month: month.charAt(0).toUpperCase() + month.slice(1),
    },
    { month, year }
  )
}

export const setMonthlyReviewCompleted = (month, year) => {
  // Send klaviyo event one time
  trackEvent(
    "Guided Reflection Completed",
    {
      guide: "Monthly Review",
      month: month.charAt(0).toUpperCase() + month.slice(1),
    },
    { month, year }
  )
}

export const setYearlyReviewCompleted = year => {
  // Send klaviyo event one time
  trackEvent(
    "Guided Reflection Completed",
    {
      guide: "Yearly Review",
      year: year,
    },
    { year }
  )
}

export const setYearlyReviewStarted = year => {
  // Send klaviyo event one time
  trackEvent(
    "Guided Reflection Started",
    {
      guide: "Yearly Review",
      year: year,
    },
    { year }
  )
}

/**
 * Sets a user's personal properties in Klaviyo
 * @param {Object} data {key: propertyValue, other: otherProp, etc...}
 */
export const setKlaviyoProperty = (data = {}) => {
  return
  setTimeout(() => {
    const result = window._learnq.push(["identify", data])
    log("log", ["setKlaviyoProperty:", { result, data }])
  }, 500)
}

/**
 * Calculates the total number of user entries that are not
 * monthly reviews.  In the case where this calculation needs
 * to be made where the cache has not been updated yet, we can
 * set `modifier` to a number like +1 (1) or -1.
 * @param {Boolean} modifier should we add or subtract from the total?
 */
export const getTotalNumberEntries = (modifier = 0) => {
  let total = 0

  try {
    let { userEntries } = client.readQuery({
      query: GET_ENTRIES,
    })
    userEntries = userEntries.filter(x => x.type !== "monthly review")
    total = modifier ? userEntries.length + modifier : userEntries.length
  } catch (e) {
    //TODO: Handle this better
    // log(
    //   "error",
    //   "getTotalNumberEntries() to retrieve total because query GET_ENTRIES failed."
    // )
  }
  return total
}

export const getUserPlan = () => {
  // Track user's current page
  let userPlan
  // If user's current route is saved in cache, retrieve it, otherwise
  // set it to null value.
  try {
    userPlan = client.readQuery({ query: USER_PLAN }).userPlan
  } catch (e) {
    userPlan = "plan-free"
  }
  return userPlan
}

export const setupServicesUser = user => {
  user = user ? user : getCurrentUser()

  window.Beacon &&
    window.Beacon("identify", {
      name: user.displayName,
      email: user.email,
      version: VERSION.number,
      plan: getUserPlan(),
    })
}

export const currentUserAction = () => {
  let actions = {}
  const path = window.location.pathname
  const hash = window.location.hash

  if (path.includes("login")) actions.logged_out = true

  if (path.includes("/compose/yearly") && !hash.includes("view"))
    actions.compose_yearly = true

  if (path.includes("/compose/monthly") && !path.includes("report"))
    actions.compose_monthly = true

  if (path === "/a/compose/highlight") actions.compose_highlight = true
  if (path === "/a/compose/lowlight") actions.compose_lowlight = true
  if (path === "/a/compose/write") actions.compose_freewrite = true

  if (
    actions.compose_highlight ||
    actions.compose_lowlight ||
    actions.compose_freewrite
  )
    actions.compose_entry = true

  return actions
}

export const useDeleteUserReview = (doDelete, setDoDelete) => {
  // const fetchPolicy = !doDelete ? "cache-only" : "network-only"
  const [deleteEntry] = useMutation(DELETE_ENTRY)

  const completedAction = () => {
    setDoDelete(null)
    navigate("/a/account")
  }

  if (doDelete) {
    const { id } = doDelete

    deleteEntry({ variables: { key: id } })
      .then(response => {
        console.log("TCL: useDeleteUserReview -> deletion success", response)

        completedAction()
      })
      .catch(error => {
        console.log("TCL: useDeleteUserReview -> entry deletion failed", error)
        alert(
          "There was an error while deleting this review.  To resolve this, please try logging out and back in.  If the issue persists, use the blue help button at the bottom right of this page to contact support."
        )

        // Done, navigate back to guides
        completedAction()
      })

    setDoDelete(null)
  }
}

export const usePermanentlyDeleteUser = (doDeleteUser, setDoDeleteUser) => {
  const [deleteUser] = useMutation(DELETE_USER)

  const completedAction = () => {
    setDoDeleteUser(null)
  }

  if (doDeleteUser) {
    console.log("TCL: usePermanentlyDeleteUser -> doDeleteUser", doDeleteUser)
    toast("⚙ Deleting account...")

    deleteUser({ variables: { uid: doDeleteUser.uid } })
      .then(response => {
        console.log(
          "TCL: usePermanentlyDeleteUser -> cloudstore deletion success",
          response
        )

        toast("👍 Account successfully deleted, you will now be logged out.")

        setTimeout(() => {
          signOut()
        }, 5000)
      })
      .catch(error => {
        console.log("TCL: useDeleteUserReview -> entry deletion failed", error)
        toast.error(
          "There was an error while deleting this account.  To resolve this, please try logging out and back in.  If the issue persists, use the blue help button at the bottom right of this page to contact support."
        )
      })

    completedAction()
  }
}

export const useExportAccountData = (
  doExportData,
  exportReady,
  setExportReady
) => {
  const exportInited = useRef(false)
  const [initExport, { data: exportData, loading }] = useLazyQuery(
    EXPORT_DATA,
    { fetchPolicy: "network-only", displayName: "Export Data Request" }
  )

  useEffect(() => {
    if (doExportData && !exportInited.current) {
      exportInited.current = true
      initExport()
    }

    if (
      exportInited.current &&
      loading === false &&
      exportData?.exportData &&
      !exportReady
    ) {
      setExportReady(exportData.exportData)
    }
  }, [doExportData, exportData])
}

export const useUpdateUserSettings = (
  doUpdateSettings,
  setDoUpdateSettings
) => {
  const [updateSettings] = useMutation(UPDATE_USER_SETTINGS)

  const completedAction = () => {
    console.log("TCL: useUpdateUserSettings -> complete")
    setDoUpdateSettings(null)
  }

  if (doUpdateSettings) {
    console.log(
      "TCL: useUpdateUserSettings -> useUpdateUserSettings",
      doUpdateSettings
    )
    toast("⚙ Updating account settings...", {
      autoClose: 1500,
    })

    completedAction()
  }
}
